import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Contact } from 'src/app/models/contact.model';
import { GraphContactService } from 'src/app/services/graph/contact.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { ModalPopupComponent } from './../modal-popup/modal-popup.component';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GraphReportService } from 'src/app/services/graph/report.service';
import { Subject } from 'rxjs';

@Component({
	selector: 'app-contact',
	templateUrl: './contact.component.html',
	styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit, OnDestroy {
	public readonly CONTACT_OPTIONS_COUNT = 8;
	public readonly SPECIFY_CATEGORY_MAX_LENGTH = 30;
	public reportCode: string | null = null;
	public reportName: string | null = null;
	@ViewChild(FormGroupDirective) private contactFormDirective?: FormGroupDirective;

	private readonly destroy$ = new Subject<void>();
	private readonly otherCategoryTranslationKey = 'ContactUs.Option7';
	private readonly trimmedRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const value = typeof control.value === 'string' ? control.value.trim() : '';
		return value.length > 0 ? null : { required: true };
	};
	private readonly trimmedMaxLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const value = typeof control.value === 'string' ? control.value.trim() : '';
		return value.length > this.SPECIFY_CATEGORY_MAX_LENGTH
			? {
				maxlength: {
					requiredLength: this.SPECIFY_CATEGORY_MAX_LENGTH,
					actualLength: value.length,
				},
			}
			: null;
	};

	readonly contactForm = new FormGroup({
		categoryCode: new FormControl('', Validators.required),
		specifyCategory: new FormControl(''),
		message: new FormControl('', Validators.required)
	});

	constructor(
		public themeService: ThemeService,
		public translateService: TranslateService,
		private dialog: MatDialog,
		private contactService: GraphContactService,
		private activatedRoute: ActivatedRoute,
		private reportService: GraphReportService,
	) { }

	ngOnInit(): void {
		this.initializeSpecifyCategoryValidation();

		this.activatedRoute.queryParams.pipe(
			tap((params) => {
				this.reportCode = params['report'] || null;
				return this.reportCode;
			}),
			filter(reportCode => reportCode !== null),
			switchMap(() => this.reportService.getReports().pipe(
				map((reports) => {
					const report = reports.find(r => r.code === this.reportCode);
					this.reportName = report ? report.title : null;
				}),
			)),
			takeUntil(this.destroy$)
		).subscribe();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	openDialog(popupMessage?: string): void {
		const message = popupMessage ?? this.translateService.instant('ContactUs.Alert');

		this.dialog.open(ModalPopupComponent, {
			width: 'rem(300px)',
			panelClass: 'popup',
			data: { popupMessage: message, popupOk: this.translateService.instant('Common.Controls.Ok') }
		});
	}

	public isOtherCategorySelected(categoryCode = this.contactForm.get('categoryCode')?.value): boolean {
		return categoryCode === this.translateService.instant(this.otherCategoryTranslationKey);
	}

	submit(): void {
		if (this.contactForm.invalid) {
			this.contactForm.markAllAsTouched();
			return;
		}

		const categoryCode = this.getSubmittedCategoryCode();
		const message = this.contactForm.get('message')?.value;
		this.contactService.submit(new Contact(categoryCode, message, this.reportName)).pipe(
			takeUntil(this.destroy$),
			tap((referenceId: string) => {
				this.openDialog(this.translateService.instant('ContactUs.AlertWithReferenceId', { referenceId }));
				this.resetContactForm();
			}),
			catchError((error) => {
				console.error(error);
				this.openDialog(this.translateService.instant('Error.ServerErrorPleaseRetry'));
				return [];
			})
		).subscribe();
	}

	private initializeSpecifyCategoryValidation(): void {
		const categoryCodeControl = this.contactForm.get('categoryCode');
		if (!categoryCodeControl) {
			return;
		}

		this.updateSpecifyCategoryValidation(categoryCodeControl.value);

		categoryCodeControl.valueChanges.pipe(
			takeUntil(this.destroy$)
		).subscribe((categoryCode) => this.updateSpecifyCategoryValidation(categoryCode));
	}

	private updateSpecifyCategoryValidation(categoryCode: string | null): void {
		const specifyCategoryControl = this.contactForm.get('specifyCategory');
		if (!specifyCategoryControl) {
			return;
		}

		if (this.isOtherCategorySelected(categoryCode)) {
			specifyCategoryControl.setValidators([
				this.trimmedRequiredValidator,
				this.trimmedMaxLengthValidator,
			]);
		} else {
			specifyCategoryControl.clearValidators();
			specifyCategoryControl.reset('');
		}

		specifyCategoryControl.updateValueAndValidity({ emitEvent: false });
	}

	private getSubmittedCategoryCode(): string | null {
		const categoryCode = this.contactForm.get('categoryCode')?.value;
		const specifyCategory = typeof this.contactForm.get('specifyCategory')?.value === 'string'
			? this.contactForm.get('specifyCategory')?.value.trim()
			: '';

		if (!this.isOtherCategorySelected(categoryCode)) {
			return categoryCode;
		}

		return `${categoryCode}: ${specifyCategory}`;
	}

	private resetContactForm(): void {
		this.contactForm.reset();
		this.contactFormDirective?.resetForm();
	}
}
